import OSS from 'ali-oss';
import * as R from 'ramda';
import {file as api} from '@/api';
import tip from '@/utils/tip';

export default {
  name: 'FileUploader',
  props: {
    title: {
      type: String, default: '',
    },
    pathName: {
      type: String,
    },
    required: {
      type: Boolean, default: false,
    },
    limit: {
      type: Number, default: 1,
    },
    canUpload: {
      type: Boolean, default: true,
    },
    canDelete: {
      type: Boolean, default: true,
    },
    canDownload: {
      type: Boolean, default: true,
    },
    beforeDownload: {
      type: Function, default: null,
    },
    uuid: {
      type: String, default: '',
    },
    uploadType: {
      type: String, default: '',
    },
    bucket: {
      type: String, default: 'dpinternal',
    },
    titleSlot: {
      type: Boolean, default: false,
    },
    acceptFileType: {
      type: String, default: '*',
    }
  },
  data() {
    return {
      loading: false,
      dialog: false,
      percentage: 0,
      curr: 1, // 当前正在上传第几个文件
      currName: '', // 当前正在上传文件名
      total: 1, // 总文件数
      speed: 1, // 当前网速
      remainTime: '0 min', // 预计耗时
      completed: 1, // 已上传
      size: 1, // 总大小
      files: [],
      dir: {},
      checkpoints: {}, // 断点续传 缓存文件
      client: {}, // ossClient
      pause: '暂停上传',
      cancel: false,
      timer: 0, // 上传耗时 计时器
      pauseShow: true, // 是否显示暂停
      cancelShow: true // 是否显示取消
    };
  },
  created() {
    // 监控 断点续传变更
    this.$watch('checkpoints', function(newValue, oldValue) {
      window.localStorage.setItem('checkpoints', JSON.stringify(newValue));
    });
  },
  computed: {
    isUploadDirectory() {
      return this.uploadType === 'directory';
    },
    paths() {
      return R.map(R.prop('path'), this.files);
    },
    isMax() {
      return this.limit <= this.files.length;
    },
  },
  methods: {
    reset() {
      this.dir = {};
      this.files = [];
      const $fileDom = this.$refs.fileInput;
      $fileDom.value = '';
    }, /***
     * 获得 oss client
     * @returns {Promise<null|Client>}
     */
    async getClient() {
      const res = await api.getToken();
      if (res) {
        const {securityToken, accessKeySecret, accessKeyId} = res;
        return new OSS({
          accessKeyId: accessKeyId,
          accessKeySecret: accessKeySecret,
          stsToken: securityToken,
          endpoint: 'oss-cn-beijing.aliyuncs.com',
          bucket: this.bucket,
          timeout: (1000 * 60 * 5)
        });
      }
      return null;
    }, /**
     * 通过url 从oss中获取文件
     * @param path
     * @returns {Promise<null|any>}
     */
    async getFile(path) {
      if (path) {
        // if (path.indexOf('等') > 0 && path.indexOf('个文件') > 0) {
        //   path = path.split(' ')[0];
        // }
        let isDicom = path.indexOf('.dcm') > 0;
        let isStl = path.indexOf('.stl') > 0;
        if (this.isUploadDirectory && !isDicom && !isStl) {
          this.dir = {
            name: path, path,
          };
        } else {
          this.loading = true;
          const client = await this.getClient();
          let result = await client.listV2({
            'max-keys': 1000, prefix: isDicom || isStl ? path.substring(0, path.lastIndexOf('/')) : path,
          });
          // console.log(result);
          if (!result.objects) {
            result = await client.listV2({
              'max-keys': 1000,
              prefix: isDicom || isStl ? path.replace('target/', '').substring(0, path.lastIndexOf('/')) : path,
            });
          }

          this.loading = false;
          let {objects} = result;
          if (R.is(Array, objects) && !R.isEmpty(objects)) {
            if (isDicom || isStl) {
              const size = R.sum(R.map(R.prop('size'), objects));
              this.dir = {
                size: size,
                name: path.split('/')[isDicom ? 2 : (isStl ? 1 : 1)] + '（' + objects.length + '个文件）',
                path,
                files: objects
              };
              return null;
            }

            return R.head(objects);
          }
          return null;
        }
      }
      return null;
    }, /**
     * 控件 设置文件
     * @param paths
     * @returns {Promise<void>}
     */
    async setFile({paths}) {
      if (R.is(Array, paths)) {
        const pArr = R.map(this.getFile, paths);
        const all = await Promise.all(pArr);

        const files = R.pipe(
          R.reject(R.isNil),
          R.map(item => R.merge(item, {
            path: item.name,
            name: R.last(R.split('/', item.name)),
          })),
        )(all);
        this.files = files;
      }
    },
    bytesToSize(bytes) {
      if (!bytes) {
        return '';
      }
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return (bytes / Math.pow(k, i)).toPrecision(3) + ' ' + sizes[i];
    }, /**
     * 上传文件
     * @param e
     * @returns {Promise<void>}
     */
    async onGetFile(e) {
      // this.loading = true;
      this.open();
      const uuid = this.uuid;
      if (!uuid && this.isUploadDirectory) {
        tip.error('未获取到uuid');
        return;
      }
      if (this.isUploadDirectory) {
        this.pauseShow = false;
        this.cancelShow = false;
      } else {
        this.pauseShow = true;
        this.cancelShow = true;
      }
      // 缓存中 读取 分片数据
      if (!this.checkpoints || Object.keys(this.checkpoints).length <= 0) {
        const checkpointsJson = window.localStorage.getItem('checkpoints');
        this.checkpoints = JSON.parse(checkpointsJson);
        if (!this.checkpoints || Object.keys(this.checkpoints).length <= 0) {
          this.checkpoints = {};
          this.timer = 0;
        }
      }
      this.total = Object.keys(this.checkpoints).length;
      this.total = this.total <= 0 ? 1 : this.total;
      if (e) {
        this.curr = 1;
        this.size = 1;
        this.speed = 1;
        // this.completed = 0;
        this.total = e.target.files.length;
      }

      this.client = await this.getClient();
      if (this.client) {
        const timestamp = Date.now();
        const pQueue = [];
        const that = this;

        R.forEach((data) => {
          let checkpoint;
          let fileName;
          let path;

          // 文件上传
          if (!data['file']) {
            fileName = data.webkitRelativePath ? data.webkitRelativePath : data.name;
            path = uuid ? `${uuid}/${fileName}` : `${this.pathName}/${timestamp}/${fileName}`;
            let keys = Object.keys(this.checkpoints);
            for (let key of keys) {
              const c = this.checkpoints[key];
              if (c.name.indexOf(data.name) > 0) {
                checkpoint = c;
                checkpoint['file'] = data;
                // data = c;
                break;
              }
            }
          } else {
            checkpoint = data;
          }
          if (checkpoint) {
            that.timer = checkpoint['timer'];
            path = checkpoint['name'];
          }
          const p = that.client.multipartUpload(((data['file'] || checkpoint) || path), (data['file'] ? data.file : data), {
            checkpoint,
            progress: function(percentage, cpt) { // 获取进度条的值
              that.checkpoints = {};
              if (cpt) {
                that.currName = cpt.file.name;
                that.size = cpt.file.size;
                // 记录用时
                cpt['timer'] = that.timer;
                data['uploadId'] = cpt.uploadId;
                if (!that.isUploadDirectory) {
                  // 只记录最后一次上传文件的 断点续传
                  that.checkpoints[cpt.uploadId] = cpt;
                }
              }
              that.percentage = Number.parseFloat((percentage * 100).toFixed(2));
              that.completed = that.size * percentage;
              // checkpoint = cpt;
            },
          }).then(() => {
            if (!data['file']) {
              data.path = path;
              data.uuid = uuid;
              this.files = R.append(data, this.files);
            } else {
              data.file.path = path;
              data.file.uuid = uuid;
              this.files = R.append(data.file, this.files);
            }
            if (data['uploadId']) {
              delete that.checkpoints[data['uploadId']];
              window.localStorage.removeItem('checkpoints');
            }
            that.curr += 1;
            that.curr = that.curr > that.total ? that.total : that.curr;
            that.completed = 0;
            that.timer = 0;
            // 上传完成 已完成追加上 当前文件大小
            return data;
          }).catch((err) => {
            // 主动取消上传
            if (err.name !== 'cancel') {
              tip.error(err);
              this.$emit('error', {paths: this.paths});
            }
          });
          pQueue.push(p);
        }, (e ? e.target.files : Object.values(this.checkpoints)));
        Promise.all(pQueue).then((files) => {
          if (!this.cancel && R.length(files) === R.length(pQueue)) {
            if (this.isUploadDirectory) {
              const {webkitRelativePath, uuid} = R.head(files);
              const name = R.head(R.split('/', webkitRelativePath));
              this.dir = {
                name, path: `${uuid}/${name}`,
              };
            }
            this.$emit('success', {dir: this.dir, paths: this.paths, files: this.files});
            tip.success('文件上传成功');
            this.dialog = false;
          }
        })
          .finally(() => {
            this.loading = false;
            const $fileDom = this.$refs.fileInput;
            $fileDom.value = '';
          });
      }
    },
    async onDownload(file) {
      let promise = this.beforeDownload ? this.beforeDownload(file) : Promise.resolve();
      promise.then(async() => {
        this.loading = true;
        const client = await this.getClient();
        const url = client.signatureUrl(file.path);
        if (url) {
          if (url.indexOf('.pdf') !== -1) {
            this.downloadfile(url, file.name, 'application/pdf');
          } else {
            let a = document.createElement('a');
            a.href = url + '&amp;name=' + file.name;
            a.download = file.name;
            a.click();
            this.$emit('download', file.name);
          }
        } else {
          tip.error('文件下载失败！');
        }
        this.loading = false;
      });
    },
    async onDownloadDir(dir) {
      navigator.clipboard.writeText(dir.map(file => file.url).join('\n'))
        .then(() => {
          alert('下载地址已复制到剪贴板');
        })
        .catch(() => {
          alert('错误,不能写入剪贴板');
        });
    },
    downloadfile(url, strFileName, strMimeType) {
      var xmlHttp = null;
      if (window.XMLHttpRequest) {
        // IE7+, Firefox, Chrome, Opera, Safari 浏览器执行代码
        xmlHttp = new XMLHttpRequest();
      }
      // 2.如果实例化成功，就调用open（）方法：
      if (xmlHttp != null) {
        xmlHttp.open('get', url, true);
        xmlHttp.responseType = 'blob';// 关键
        xmlHttp.send();
        const that = this;
        xmlHttp.onreadystatechange = function() {
          if (xmlHttp.readyState === 4) { // 4表示执行完成
            if (xmlHttp.status === 200) { // 200表示执行成功
              that.download(xmlHttp.response, strFileName, strMimeType);
            }
          }
        }; // 设置回调函数
      }
    },
    download(data, strFileName, strMimeType) {
      // eslint-disable-next-line one-var
      var self = window, // this script is only for browsers anyway...
        u = 'application/octet-stream', // this default mime also triggers iframe downloads
        m = strMimeType || u, x = data, D = document, a = D.createElement('a'), z = function(a) {
          return String(a);
        },

        B = self.Blob || self.MozBlob || self.WebKitBlob || z,
        BB = self.MSBlobBuilder || self.WebKitBlobBuilder || self.BlobBuilder, fn = strFileName || 'download', blob, b,
        fr;

      // if(typeof B.bind === 'function' ){ B=B.bind(self); }

      if (String(this) === 'true') { // reverse arguments, allowing download.bind(true, "text/xml", "export.xml") to act as a callback
        x = [x, m];
        m = x[0];
        x = x[1];
      }

      // go ahead and download dataURLs right away
      // eslint-disable-next-line no-useless-escape
      if (String(x).match(/^data\:[\w+\-]+\/[\w+\-]+[,;]/)) {
        return navigator.msSaveBlob // IE10 can't do a[download], only Blobs:
          ? navigator.msSaveBlob(d2b(x), fn) : saver(x); // everyone else can save dataURLs un-processed
      }// end if dataURL passed?

      try {
        blob = x instanceof B ? x : new B([x], {type: m});
      } catch (y) {
        if (BB) {
          b = new BB();
          b.append([x]);
          blob = b.getBlob(m); // the blob
        }
      }

      function d2b(u) {
        // eslint-disable-next-line one-var
        var p = u.split(/[:;,]/), t = p[1], dec = p[2] === 'base64' ? atob : decodeURIComponent, bin = dec(p.pop()),
          mx = bin.length, i = 0, uia = new Uint8Array(mx);

        for (i; i < mx; ++i) uia[i] = bin.charCodeAt(i);

        return new B([uia], {type: t});
      }

      function saver(url, winMode) {
        if ('download' in a) { // html5 A[download]
          a.href = url;
          a.setAttribute('download', fn);
          a.innerHTML = 'downloading...';
          D.body.appendChild(a);
          setTimeout(function() {
            a.click();
            D.body.removeChild(a);
            if (winMode === true) {
              setTimeout(function() {
                self.URL.revokeObjectURL(a.href);
              }, 250);
            }
          }, 66);
          return true;
        }

        // do iframe dataURL download (old ch+FF):
        var f = D.createElement('iframe');
        D.body.appendChild(f);
        if (!winMode) { // force a mime that will download:
          // eslint-disable-next-line no-useless-escape
          url = 'data:' + url.replace(/^data:([\w\/\-\+]+)/, u);
        }

        f.src = url;
        setTimeout(function() {
          D.body.removeChild(f);
        }, 333);
      }// end saver

      if (navigator.msSaveBlob) { // IE10+ : (has Blob, but not a[download] or URL)
        return navigator.msSaveBlob(blob, fn);
      }

      if (self.URL) { // simple fast and modern way using Blob and URL:
        saver(self.URL.createObjectURL(blob), true);
      } else {
        // handle non-Blob()+non-URL browsers:
        if (typeof blob === 'string' || blob.constructor === z) {
          try {
            return saver('data:' + m + ';base64,' + self.btoa(blob));
          } catch (y) {
            return saver('data:' + m + ',' + encodeURIComponent(blob));
          }
        }

        // Blob but not URL:
        fr = new FileReader();
        fr.onload = function(e) {
          saver(this.result);
        };
        fr.readAsDataURL(blob);
      }
      return true;
    },
    onDelete(file, idx) {
      this.$confirm('是否删除文件？').then(async() => {
        this.loading = true;
        const client = await this.getClient();
        if (client) {
          await client.delete(file.path);
          tip.success('删除成功');
          this.files = R.remove(idx, 1, this.files);
          this.$emit('deleted', {path: file.path, paths: this.paths});
        }
        this.loading = false;
      });
    },
    onDirDelete() {
      this.$confirm('是否删除文件？').then(() => {
        this.reset();
        this.$emit('deleted', {dir: this.dir, beforePaths: R.clone(this.paths)}); // 这里给出删除前的文件集合，因为删除文件夹是全部文件都删除
      });
    },
    open() {
      this.cancel = false;
      this.dialog = true;
      this.percentage = 0;
      const that = this;
      const interval = setInterval(() => {
        if (this.cancel) {
          clearInterval(interval);
          return;
        }
        if (that.isUploadDirectory) {
          if (that.curr >= that.total) {
            clearInterval(interval);
            return;
          }
        } else if (this.percentage >= 100) {
          clearInterval(interval);
          return;
        }
        that.timer += 0.1;
        that.speed = (that.completed / that.timer);
        that.speed = that.speed === 0 ? 1 : that.speed;
        // 预计时间 = （总大小 - 已完成大小） /速度
        if (that.speed > 1) {
          let remainTime = (that.size - that.completed) / that.speed;
          that.remainTime = (remainTime / 60).toFixed(0) + ' min';
          if (that.remainTime === '0 min') {
            that.remainTime = remainTime.toFixed(0) + ' s';
          }
        } else {
          that.remainTime = '0 min';
        }
      }, 100);
    },
    onPause() {
      if (!this.client) {
        tip.error('上传失败，请重试');
        return;
      }
      if (this.pause === '暂停上传') {
        this.pause = '继续上传';
        this.client.cancel();
        this.cancel = true;
        this.curr = 1;
      } else {
        this.pause = '暂停上传';
        this.onGetFile();
      }
    },
    onCancel() {
      tip.error('取消上传');
      this.pause = '暂停上传';
      this.client.cancel();
      // 计时器 重置
      this.timer = 0;
      this.cancel = true;
      this.dialog = false;
    }
  },
};



// WEBPACK FOOTER //
// ./src/comp/FileUploader/_.js